<template>
  <div class="protocol">
    <div class="bt" style="text-align: center">
      {{ appName }}'s Privacy Policy
    </div>
    <div>
      Welcome to use {{ appName }} (hereinafter referred to as the "Platform" or
      "APP"). This platform is provided and operated by
      {{ appName }} (hereinafter referred to as "we"). We highly value your
      privacy protection and are always committed to safeguarding the security
      and legitimate rights of your personal information. This Privacy Agreement
      will detail the specific ways and principles we follow in collecting,
      using, storing, and sharing your personal information. If you do not agree
      with the content of this Agreement, please do not use the services of this
      platform. <br /><br />
      Please carefully read and fully understand this Privacy Policy before
      using the platform, and clearly know our rules for handling your personal
      information. Some functions of this platform may provide you with
      additional service descriptions through the interface. This Privacy Policy
      applies to our collection, use of your personal information, and data
      processing activities related to your platform activities (in specific
      scenarios, we may provide supplementary notices, options, or additional
      explanations).
      <br /><br />
      When you access or use this platform, you are deemed to have fully read,
      understood, and agreed to all the terms of this Privacy Policy. We may
      make irregular revisions to the content of this Policy in accordance with
      updates to laws and regulations or business adjustments. If you do not
      agree to the revised terms of the Policy, you should immediately stop
      accessing or using this platform.
      <br /><br />
    </div>

    <div class="bt">1. Information We Collect</div>
    <div>
      During your account registration and use of the platform services, we will
      collect the following types of information based on service requirements:
      <br /><br />
    </div>
    <div class="bt subtitle">1. Information you voluntarily provide</div>
    <div>
      - Account registration information: includes the nickname, gender, date of
      birth, country/region, age, email address, password you submitted when
      creating your {{ appName }} account, the avatar, video provided when
      registering through social media accounts (such as Facebook), and the tags
      you set for your account; <br /><br />
      - Profile Information: Content that you set on your {{ appName }} personal
      homepage and is visible to other users, such as photos, gender, etc.;
      <br /><br />
      - User Generated Content (UGC): Comments, text, messages, pictures,
      images, videos, audio, code, and other data or creatives that you upload,
      post, disseminate, or stream while using the service;
      <br /><br />
      - Payment-related information: includes payment account info such as bank
      account info and PayPal account info you provided for purchasing services,
      as well as records such as transaction numbers and transaction history
      after purchasing paid services;
      <br /><br />
      - Communication and Interaction Information: Information you submit when
      participating in in-app surveys or activities, and communication content
      used for account verification, feedback handling, or complaint resolution.
      <br /><br />
      <div class="bt subtitle">2. Information obtained from other sources</div>
      <br /><br />
      - Social Network Information: If you choose to log in to or link to the
      platform via a third-party social network account (such as Facebook), we
      may obtain your contact list and information related to platform usage
      from that social network;
      <br /><br />
      - Third Party Cooperation Information: Information such as your age and
      demographic characteristics obtained from third-party AD Networks, Data
      Analysis service providers, business directories, or public channels.
      <br /><br />
      <div class="bt subtitle">3. Automatically Collected Information</div>
      <br /><br />
      - Device and Usage Data: Includes your IP address, Unique Device
      Identifier, browsing history (such as content you viewed within the
      platform), cookies (see details below), mobile carrier information, time
      zone settings, and device attributes such as device model, screen
      definition, operating system version, application version, file name and
      type;
      <br /><br />
      - Location-related data: approximate location information obtained based
      on your IP address; after your authorization, Global Position System (GPS)
      data may be collected (used only to confirm your city, and only city
      information will be transmitted to the server);
      <br /><br />
      - Message data: The content of messages you send and receive through the
      platform's messaging function, as well as the message's sending time,
      receiving time, read status, and information about the communication
      object (please note: Messages you send to other users can be viewed by the
      recipient, and we are not responsible for the actions of other users in
      using or disclosing such messages);
      <br /><br />
      - Content Metadata: Descriptive data automatically attached when you
      upload user content, including how the content was created, creation time,
      author information, format, associated account name/User ID, hashtags you
      added, subtitles, etc.;
      <br /><br />
      - Virtual Item Purchase Information: When you purchase platform coins
      through Google Play or other authorized channels, we will record your
      purchase behavior, purchase time, and amount (without collecting financial
      or billing details related to the transaction, which are only used to
      confirm the arrival of coins).
      <br /><br />
    </div>
    <div class="bt">2. How We Use Your Information</div>
    <div>
      We will use your information strictly in accordance with the following
      purposes, ensuring compliance with legal and regulatory requirements and
      the scope of your authorization:
      <br /><br />
      1. Service Fulfillment and Management: Implement platform service terms,
      conduct daily platform operations, maintenance, and troubleshooting,
      perform Data Analysis, testing, research, and statistical surveys, and
      collect your usage feedback to optimize services;
      <br /><br />
      2. Personalized Service Provision: Based on your setting preferences and
      usage habits, we will push personalized content to you, such as providing
      adapted services based on the country/region you select and recommending
      content that you may be interested in;
      <br /><br />
      3. Product Improvement and Development: Optimize platform functions based
      on user usage data, develop new services or enhance the quality of
      existing services;
      <br /><br />
      4. Social Function Support: Ensures social interaction among users,
      supports content sharing, downloading, and interactive operations, helping
      you connect with other users;
      <br /><br />
      5. Security Assurance: Detect and prevent abuse, fraud, and illegal or
      irregular activities within the platform to maintain the platform's safe
      and stable operation;
      <br /><br />
      6. Age Verification: Confirm whether you meet the minimum age requirement
      for using the platform's services as required by laws and regulations;
      <br /><br />
      7. Communication and Notification: Inform you of service changes,
      important announcements, event notifications, and other information to
      maintain effective communication with you;
      <br /><br />
      8. Event Operation: When you participate in activities such as
      competitions, lotteries, promotions, etc., verify the winning eligibility
      and distribute prizes;
      <br /><br />
      9. Policy Execution: Maintain the normal order of the platform in
      accordance with the platform's terms, rules, and this Privacy Policy;
      <br /><br />
      10. Algorithm Optimization: Provide data support for platform algorithms
      to improve service matching efficiency and user experience;
      <br /><br />
      11. Information Integration: Integrate the information collected for the
      above purposes for other legitimate uses that have been disclosed to you
      or approved by you.
      <br /><br />
    </div>
    <div class="bt">3. How We Share Your Information</div>
    <div>
      We commit to handling your information carefully and will only share your
      personal information with third parties in the following necessary
      circumstances:
      <br /><br />
      1. Third-party service providers: To ensure the operation of the platform,
      we will cooperate with third parties that provide services such as
      research, payment processing, transaction execution, database maintenance,
      competition management, technical support, data storage, disaster
      recovery, search engine optimization, marketing promotion, etc., and share
      necessary information with them (such third parties are required to comply
      with confidentiality obligations and shall not use the information for
      other purposes);
      <br /><br />
      2. Legal Requirements: Disclose your information to protect the legitimate
      rights and safety of us, affiliated companies, users, or the public in
      accordance with subpoenas, court judgments, legal proceedings, requests
      from law enforcement agencies, or government inquiries;
      <br /><br />
      3. Your authorization: Information will be used for specific purposes when
      it has been clearly informed to you at the time of collection or when your
      written/affirmative consent has been obtained;
      <br /><br />
      4. Third - Party Platform Interaction: When you log in to the platform
      through third - party services (such as Facebook, Google) or share
      platform usage information with third parties, the third party may collect
      information about your platform activities and disclose your usage to its
      users in accordance with its Privacy Policy;
      <br /><br />
      5. Participation in Public Activities: When you participate in public
      activities on the platform, the information you share may be viewed,
      collected, or used by other users. Please be cautious when disclosing
      personal information, and we will not be responsible for the information
      you voluntarily make public.
      <br /><br />
    </div>
    <div class="bt subtitle">4. Your Rights</div>
    <div>
      1. Cookie Management: You can refuse or disable cookies by adjusting your
      browser settings (the settings vary for different browsers; it is
      recommended to refer to the browser's help documentation). Some cookies
      need to be set separately through browser and device/app permissions (such
      as login-related cookies), and the settings only take effect for the
      current browser/device. Refusing or disabling cookies may affect the
      normal use of some platform features;
      <br /><br />
      2. Access and Update of Personal Information: After logging into your
      account, you can view and modify some of the personal information you have
      submitted through the platform's functions;
      <br /><br />
      3. Account and Data Deletion: If you need to delete your account and
      related data, please send a verification request to our support team
      (Email: {{ appEmail }}). We may require you to provide proof of account
      ownership (such as identification documents matching account info, recent
      transaction records, device verification codes, etc.). After verification
      is successful, we will process the deletion request within 72 hours and
      provide you with proof of data erasure through the compliance portal. We
      only retain your information within the scope required or permitted by
      law, and you have the right to apply for permanent deletion of your
      account and data at any time.

      <br /><br />
    </div>
    <div class="bt subtitle">5. Data Security</div>
    <div>
      We implement multiple security measures at the management, technical, and
      physical levels to protect your personal information from unauthorized
      access and disclosure:
      <br /><br />
      - Strictly limit the information access rights of authorized employees,
      allowing them to access information only within the scope of their job
      responsibilities;
      <br /><br />
      - Encryption technology is used to protect the transmission of information
      between devices and systems, and measures such as firewalls are employed
      to prevent unauthorized access.
      <br /><br />
      Please note: Internet transmission has inherent risks, and even if we take
      reasonable security measures, we cannot completely eliminate the security
      risks associated with information storage and transmission. Please submit
      information with caution and reasonably assess the scope of information
      disclosure.
      <br /><br />
    </div>
    <div class="bt">6. Data Retention</div>
    <div>
      We will retain your information for the necessary period during which we
      provide services to you; if required by laws and regulations, or to
      safeguard legitimate rights and interests (such as establishing,
      exercising, or defending legal rights), the retention period may be
      extended. After you terminate using the platform, we will delete your
      information or anonymize it to protect your privacy.
      <br /><br />
    </div>
    <div class="bt">7. Child Protection</div>
    <div>
      This platform's services are not applicable to users under 18 years old,
      and we will not deliberately collect personal information from children
      under 18. If you are a parent or guardian of a child and discover that the
      child has disclosed personal information to us, please contact us
      immediately via email at {{ appEmail }}. Once we confirm the collection of
      a child's information, we will immediately stop processing and delete the
      relevant data as soon as possible. <br /><br />
      1.Prohibited Content Rules (Related to Child Protection)
      <br /><br />
      Users are strictly prohibited from creating, uploading, or disseminating
      the following content that endangers children:
      <br /><br />
      - Improper interactions with children (such as language involving
      touching, caressing, or containing abusive content in oral or audio form);
      <br /><br />
      - Enticing children (such as making online acquaintance with children with
      the intention of engaging in sexual contact or exchanging pornographic
      content either offline or online);
      <br /><br />
      - Depicting minors in a sexualized manner (such as content depicting or
      promoting child , or images that may lead to child sexual exploitation);
      <br /><br />
      - Sexual extortion (such as threatening and blackmailing children with
      their private photos);
      <br /><br />
      - Trafficking in children (such as promoting, luring children for
      commercial exploitation);
      <br /><br />
      - Adult-themed content targeting children (such as content with excessive
      violence and gore, content that describes/encourages dangerous behavior,
      or content that promotes negative body/self-image, including the
      entertainment-oriented presentation of plastic surgery, weight loss, and
      other cosmetic effects).
      <br /><br />
      2. Children's Information Privacy Protection
      <br /><br />
      - Information Collection Limitation: When children register, we only
      collect the username, password, birthday, as well as the device ID, IP
      address, browser type, country-level location, and application activity
      data automatically generated by the device;
      <br /><br />
      - Information Usage Restrictions: Only used for providing services (such
      as identity verification), ensuring security (such as risk prevention),
      troubleshooting, and compliance review, not for commercial purposes
      (including advertising and marketing), and not shared with third parties
      for commercial purposes;
      <br /><br />
      - Information sharing restrictions: Information will only be disclosed
      when legally permitted/required (such as in response to court orders, law
      enforcement requests, or to protect child safety and maintain platform
      security);
      <br /><br />
      - Information Security: Take measures such as encryption to protect
      children's information, and do not sell children's information to third
      parties;
      <br /><br />
      - Exercise of Rights: You may apply for access to or deletion of
      children's information via email, and we will verify and respond in
      accordance with the law, without discriminatory treatment due to your
      exercise of privacy rights.
      <br /><br />
      3. Handling of Child Material (CSAM)
      <br /><br />
      If it is found that the platform contains child content, we will
      immediately take measures such as deleting the content, restricting /
      banning accounts, and disabling devices.
      <br /><br />
      4. User feedback mechanism
      <br /><br />
      If you find any content on the platform that endangers children, please
      report it to us via email at {{ appEmail }}, and we will handle it
      promptly. <br /><br />
    </div>

    <div class="bt">8. Device Permission Invocation and Third-Party SDKs</div>
    <div>
      1.Device Permission Instructions
      <br /><br />
      We will invoke the necessary permissions of your device (such as location
      permission, storage permission, microphone permission, etc.) based on the
      requirements of service functions. The specific scenarios for invoking
      permissions will be explained to you when using the functions. You can
      independently enable or disable permissions in your device settings (the
      setting paths vary for different devices; it is recommended to refer to
      the instructions from the device manufacturer).
      <br /><br />
    </div>
    <div class="table-container">
      <table>
        <tr>
          <td>Device Permission Functions</td>
          <td>Description of the device permissions function</td>
          <td>Description of functions and scenarios</td>
        </tr>
        <tr>
          <td>Network State</td>
          <td>
            We collect network connection state (Android. Permission.
            ACCESS_NETWORK_STATE) and WiFi state (Android. Permission.
            ACCESS_WIFI_STATE), as well as changes in network state (Android.
            Permission. CHANGE_NETWORK_STATE) or changes to WiFi state (Android.
            Permission. CHANGE_WIFI_STATE)
          </td>
          <td>Ensure application functionality and optimize network usage.</td>
        </tr>
        <tr>
          <td>Internet Access</td>
          <td>We need INTERNET access (android. Permission. INTERNET)</td>
          <td>Turn on Internet access for real-time reading and updates.</td>
        </tr>
        <tr>
          <td>Bluetooth Status</td>
          <td>
            We might visit BLUETOOTH state (android. Permission. BLUETOOTH and
            android. Permission. BLUETOOTH_CONNECT)
          </td>
          <td>Support Bluetooth device connection and data transmission.</td>
        </tr>
        <tr>
          <td>Audio Settings and Recordings</td>
          <td>
            We may change the audio Settings (android. Permission.
            MODIFY_AUDIO_SETTINGS) and recording function (android. Permission.
            RECORD_AUDIO)
          </td>
          <td>
            Provides a better audio experience with support for voice chat and
            other voice features.
          </td>
        </tr>
        <tr>
          <td>Camera</td>
          <td>We may visit the CAMERA (android. Permission. CAMERA)</td>
          <td>
            Captured when content is posted; captured when chatting with other
            users; captured during registration and identity verification; and
            when using other capture features.
          </td>
        </tr>
        <tr>
          <td>Storage Reading</td>
          <td>
            We may access the external storage of the device
            (android.permission.READ_EXTERNAL_STORAGE)
          </td>
          <td>
            To access images, files, and other content stored on your device,
            you agree that such access is primarily for accessibility purposes,
            including, but not limited to, posting information, uploading
            profile pictures or other images, uploading files, and logging
            crashes locally for diagnostic purposes.
          </td>
        </tr>
        <tr>
          <td>Storage Wrting</td>
          <td>
            We may access the external storage of the device
            (android.permission.WRITE_EXTERNAL_STORAGE)
          </td>
          <td>
            Writing, downloading, saving, caching, modifying or deleting images,
            files and other information is only used to enhance the user
            experience and ensure the normal operation of the application.
          </td>
        </tr>
        <tr>
          <td>Media Access</td>
          <td>We may access audio (android.permission.READ_MEDIA_AUDIO)</td>
          <td>
            Assist you to complete the collection and release of audio functions
          </td>
        </tr>
        <tr>
          <td>Notifications and Reminders</td>
          <td>
            We may send notification (android. Permission. POST_NOTIFICATIONS
            and com. Google. Android. The c2dm. Permission. The RECEIVE)
          </td>
          <td>App updates and push messages</td>
        </tr>
      </table>
    </div>
    <br /><br />
    <div>
      2. Third-Party SDK Instructions
      <br /><br />
      To achieve data statistics, anti-fraud, safeguard account and transaction
      security, and enhance the chat and social experience, we may integrate
      third-party SDKs, including but not limited to:
      <br /><br />
      - Yunxin SDK (for instant messaging functionality support);
      <br /><br />
      - Yidun SDK (used for risk prevention and control, for details of the
      Privacy Policy, see:https://dun.163.com/clause/privacy).
      <br /><br />
      Third-party SDKs will process information strictly in accordance with
      their Privacy Policy and our authorization, and we will oversee their
      compliance in using your information.
      <br /><br />
    </div>
    <div class="bt">9. Policy Changes</div>
    <div>
      We may revise this Privacy Policy in accordance with updates to laws and
      regulations or business adjustments. If there are significant changes to
      the content of the agreement, we will notify you through platform
      announcements; to ensure you stay informed of the latest rules, we
      recommend that you regularly review this agreement. Continuing to use the
      platform after the agreement is revised shall be deemed as your consent to
      the revised terms; if you do not agree, you should immediately stop using
      the service.
      <br /><br />
    </div>
    <div class="bt">10. Contact Us</div>
    <div>
      If you have any questions, comments, or suggestions regarding this Privacy
      Policy, privacy protection measures, policy implementation, or platform
      operations, please contact us via the official email address
      {{ appEmail }}. We will review and respond to your requests within a
      reasonable period. <br /><br />
    </div>
  </div>
</template>

<script>
export default {
  data() {
    return {};
  },
  created() {},
};
</script>

<style scoped>
.table-container {
  width: 100%; /* 或任何具体宽度 */
  overflow-x: auto;
}
table {
  border-collapse: collapse; /* 合并边框 */
  max-width: 100%;
  width: 100%;
  margin: 0.2267rem 0; /* 外边距 */
  font-family: Arial, sans-serif; /* 字体 */
}
td {
  padding: 0.2267rem; /* 内边距 */
  border: 1px solid #ddd; /* 边框 */
  word-wrap: break-word; /* 允许长单词换行 */
  white-space: normal; /* 允许内容换行 */
}

.protocol {
  /* overflow: hidden; */
  width: 100%;
  font-size: 0.9rem;
  margin-bottom: 0.4667rem;
  color: #666;
  padding-left: 0.4rem;
  padding-right: 0.4rem;
  box-sizing: border-box;
}
.bt {
  font-size: 1.3rem;
  margin: 0.2rem 0px;
  color: #000;
}
.subtitle {
  font-size: 1.1rem;
  color: #333;
}
</style>
